// shared-lib/cabinet-runs/bayWidths.js
//
// The widths ONE lower bay may take — the one rule every surface that sets a
// bay's width or front asks: the bay-edge drag, the panel's width chips and
// front picker, the override write boundary and the family re-fit. Pure.
//
// A family's width list (`spec.widths`) says what boxes it sells in general.
// A family with a size CATALOG (Venus) sells each KIND of box at its own
// widths: drawer bases 12-36", sink bases 24-42" without a 27", base cabinets
// 9-42" (the 9" a full-door unit), no two-drawer vanity base at all. Asked of
// the family list alone, the drag pinned a 39" drawer base, which the drawing
// could only show as a generic stack and the estimate could only price as a
// part Venus does not make. So the list is narrowed here, and only here, to
// the widths the catalog prices the box at — read through
// `resolveCabinetSku`, the same reading the estimate and the drawn drawer
// stack use (`drawerStackFor`), so the three cannot disagree.
//
// A family with no catalog — every generic one — gets exactly the family
// rule it had before: the list, or any width for Generic Custom.

const {
  familyOf,
  familyWidthsIn,
  familyFillOpts,
  familySellsDiscreteWidths,
  narrowestModuleIn,
} = require('./families');
const { widestModuleIn } = require('./moduleSolver');
const {
  familySizes,
  cabinetKindOf,
  resolveCabinetSku,
} = require('./vendorSizes');
const { applyBayType } = require('./bayOverrides');
const { profileOf, runDepthSpec, runBodyHeight } = require('./profiles');
const {
  isValanceBay,
  valanceStyleFor,
  valanceWidthsIn,
} = require('./valanceBays');

const IN = 0.0254;
/** Two widths this close (inches) are the same width. */
const SAME_IN = 1e-6;

/** What a catalog kind is called in a sentence: [one, many]. */
const KIND_WORDS = Object.freeze({
  base: ['base cabinet', 'base cabinets'],
  'drawer-base': ['drawer base', 'drawer bases'],
  'two-drawer-base': ['two-drawer base', 'two-drawer bases'],
  'sink-base': ['sink base', 'sink bases'],
  'farm-sink-base': ['farmhouse sink base', 'farmhouse sink bases'],
  'vanity-base': ['vanity base', 'vanity bases'],
  'vanity-drawer-base': ['vanity drawer base', 'vanity drawer bases'],
  'vanity-two-drawer-base': [
    'two-drawer vanity base',
    'two-drawer vanity bases',
  ],
  'vanity-sink-base': ['vanity sink base', 'vanity sink bases'],
});

const wordsFor = (kind) => KIND_WORDS[kind] || [kind, `${kind}s`];

/** A width in inches as a sentence says it: 9", 15.8". */
const said = (n) => `${+n.toFixed(2)}"`;

/** Half-inch precision, as the catalog reads a box (`resolveSku`). */
const asIn = (m) => Math.round((m / IN) * 2) / 2;

/**
 * The box `cabinet` would be wearing `front` at `widthIn` — a copy; the
 * cabinet is never touched. `front` absent keeps the cabinet's own front.
 */
function probeOf(cabinet, { front, widthIn } = {}) {
  const probe = {
    ...cabinet,
    category: { ...(cabinet.category || {}) },
    dimensions: {
      ...(cabinet.dimensions || {}),
      ...(widthIn != null ? { x: widthIn * IN } : {}),
    },
  };
  if (front) applyBayType(probe, front);
  return probe;
}

/**
 * Is this a box the catalog sells by kind and width? A LOWER cabinet with a
 * door or drawer front. Not a filler (cut from stock to any width), not a
 * tall unit (a span packed as the line's pantry widths), not an open shelf,
 * and not an upper, which the upper tier fits to the line on its own terms
 * (`fitUpperWidthIn`).
 */
function isCatalogBox(probe) {
  const c = probe.category || {};
  return probe.type === 'lower-cabinet' && !c.filler && !c.tall && !c.openShelf;
}

/**
 * The widths one bay may take.
 *
 * @param {Object} run      the stored or hydrated run (its family and profile)
 * @param {Object} cabinet  the bay's box as built (front, depth, label)
 * @param {{front?: string}} opts  ask as if the bay wore this front instead
 * @returns {{
 *   widthsIn: number[]|null,  ascending; null = any width (Generic Custom)
 *   minIn: number|null, maxIn: number|null,  the range a drag may cover
 *   line: null|{ vendor, kind, one, many },  set when the family's CATALOG
 *     decides, so a refusal can name the line and the kind
 *   fullDoorOnlyIn: number[],  family widths the line makes for this kind
 *     only as a full-height door with no drawer (Venus's 9" BF0924), so a
 *     refusal there can say so rather than "makes no base cabinet 9" wide"
 *   noneReason: string|null,  when the line makes the kind at NO width and
 *     the catalog gave one reason for every width it was asked — a reason
 *     that is not about width, such as a Venus vanity bay that is not 21"
 *     deep — so the refusal can say it rather than "makes no vanity bases"
 * }}
 */
function bayWidthRule(run, cabinet, { front } = {}) {
  const family = familyOf(run);
  // A valance bay is as wide as a valance of its style comes in
  // (`valanceBays.js`), not a box the line sells.
  if (front === 'valance' || (front == null && isValanceBay(cabinet))) {
    const widthsIn = valanceWidthsIn(family, valanceStyleFor(run, cabinet));
    return {
      widthsIn,
      minIn: widthsIn.length ? widthsIn[0] : null,
      maxIn: widthsIn.length ? widthsIn[widthsIn.length - 1] : null,
      line: null,
      fullDoorOnlyIn: [],
      noneReason: null,
    };
  }
  const familyMin = narrowestModuleIn(run) || 9;
  const familyMax = widestModuleIn(familyFillOpts(run));
  const familyRule = {
    widthsIn: familySellsDiscreteWidths(run)
      ? [...familyWidthsIn(run)].sort((a, b) => a - b)
      : null,
    minIn: familyMin,
    maxIn: familyMax,
    line: null,
    fullDoorOnlyIn: [],
    noneReason: null,
  };
  if (!familySizes(family) || !cabinet) return familyRule;
  const probe = probeOf(cabinet, { front });
  if (!isCatalogBox(probe)) return familyRule;
  const ctx = { profileId: profileOf(run).id };
  const kind = cabinetKindOf(family, probe, ctx);
  const read = (familyRule.widthsIn || []).map((w) => ({
    w,
    r: resolveCabinetSku(family, probeOf(probe, { widthIn: w }), ctx),
  }));
  const made = read.filter(({ r }) => !!r.sku).map(({ w }) => w);
  const [one, many] = wordsFor(kind);
  const reasons = new Set(read.map(({ r }) => r.reason));
  return {
    widthsIn: made,
    minIn: made.length ? Math.max(familyMin, made[0]) : null,
    maxIn: made.length ? Math.min(familyMax, made[made.length - 1]) : null,
    line: { vendor: family.vendor || family.name, kind, one, many },
    fullDoorOnlyIn: read.filter(({ r }) => r.fullDoorOnly).map(({ w }) => w),
    noneReason:
      !made.length && read.length > 1 && reasons.size === 1
        ? [...reasons][0] || null
        : null,
  };
}

/** "Venus Cabinetry makes 9" bases only as a full-height door, with no drawer". */
const fullDoorOnlySentence = (vendor, widthIn) =>
  `${vendor} makes ${said(widthIn)} bases only as a full-height door, with no drawer`;

/**
 * Does `rule` allow a box `widthIn` wide? A line's catalog reads a box to the
 * half inch (`resolveSku`), so a 23.9" box is its 24" part; a family's own
 * list is matched exactly, as `snapWidthToFamily` always has.
 */
function allowsWidthIn(rule, widthIn) {
  if (!rule.widthsIn) return true;
  const w0 = rule.line ? asIn(widthIn * IN) : widthIn;
  return rule.widthsIn.some((w) => Math.abs(w - w0) <= SAME_IN);
}

/** The width in `widths` nearest `widthIn`, ties UP; null for none. */
function nearestIn(widths, widthIn) {
  if (!widths.length) return null;
  return widths.reduce((best, w) => {
    const d = Math.abs(w - widthIn);
    const bd = Math.abs(best - widthIn);
    return d < bd - 1e-9 || (Math.abs(d - bd) <= 1e-9 && w > best) ? w : best;
  });
}

/**
 * The width a WRITE becomes under the rule: the nearest allowed width, ties
 * up — a bay the user widened must not come back narrower. Unchanged under
 * Generic Custom. For a family with no catalog this is exactly
 * `snapWidthToFamily`; a kind the line makes at no width returns the value
 * as given, since there is nothing nearer to offer (the front picker is what
 * refuses that case).
 */
function nearestAllowedIn(rule, widthIn) {
  if (!rule.widthsIn || !Number.isFinite(widthIn)) return widthIn;
  return nearestIn(rule.widthsIn, widthIn) ?? widthIn;
}

/**
 * The width a DRAG asking for `widthIn` lands on: inside the rule's range and
 * no wider than `atMostIn` (the free space the bay can grow into), then the
 * nearest allowed width there, ties up. Null when nothing allowed fits.
 */
function fitAllowedIn(rule, widthIn, { atMostIn = Infinity } = {}) {
  if (rule.minIn == null || !Number.isFinite(widthIn)) return null;
  const hi = Math.min(rule.maxIn, atMostIn);
  if (hi < rule.minIn - SAME_IN) return null;
  const clamped = Math.min(hi, Math.max(rule.minIn, widthIn));
  if (!rule.widthsIn) return clamped;
  const inRange = rule.widthsIn.filter(
    (w) => w >= rule.minIn - SAME_IN && w <= hi + SAME_IN
  );
  return nearestIn(inRange, clamped);
}

/**
 * Why the line makes no box of this kind `widthIn` wide — "Venus Cabinetry
 * makes no drawer base 39" wide" — or null when it does, or when the family
 * has no catalog to ask. The panel's greyed chips and fronts say this.
 */
function widthUnavailableReason(rule, widthIn) {
  if (!rule.line || allowsWidthIn(rule, widthIn)) return null;
  const w = asIn(widthIn * IN);
  if ((rule.fullDoorOnlyIn || []).some((d) => Math.abs(d - w) <= SAME_IN)) {
    return fullDoorOnlySentence(rule.line.vendor, w);
  }
  return `${rule.line.vendor} makes no ${rule.line.one} ${said(w)} wide`;
}

/**
 * Why this bay cannot wear `front` at the width it has — "Venus Cabinetry
 * makes no drawer base 39" wide" — or null when the line makes that box, or
 * has no catalog. Asked of the box as it would be (`applyBayType` on a copy),
 * at its own width, the way the estimate prices it.
 */
function frontWidthUnavailableReason(run, cabinet, front) {
  if (!cabinet?.dimensions?.x) return null;
  if (front === 'tall') return tallUnavailableReason(run, cabinet);
  const rule = bayWidthRule(run, cabinet, { front });
  if (!rule.line) return null;
  const family = familyOf(run);
  const probe = probeOf(cabinet, { front });
  const ctx = { profileId: profileOf(run).id };
  const r = resolveCabinetSku(family, probe, ctx);
  if (r.sku) return null;
  const w = asIn(probe.dimensions.x);
  if (r.fullDoorOnly) return fullDoorOnlySentence(rule.line.vendor, w);
  return `${rule.line.vendor} makes no ${rule.line.one} ${said(w)} wide`;
}

/**
 * Why this bay cannot wear a TALL front — "Venus Cabinetry makes no tall
 * pantry 36" wide" — or null when the line makes one at this width, or has no
 * catalog. A tall is not a catalog box the width rule reads (`isCatalogBox`):
 * the fill stands it at one of the line's pantry heights (`tallSpanSpec`
 * snaps to `tallHeightsIn`), so it is asked at each of those, never at the
 * base height the box has when the picker asks (#2690). A line that lists no
 * tall heights says nothing.
 */
function tallUnavailableReason(run, cabinet) {
  const family = familyOf(run);
  if (!familySizes(family)) return null;
  const heightsIn = familyFillOpts(run).tallHeightsIn || [];
  if (!heightsIn.length) return null;
  const probe = probeOf(cabinet, { front: 'tall' });
  const ctx = { profileId: profileOf(run).id };
  const made = heightsIn.some(
    (h) =>
      resolveCabinetSku(
        family,
        { ...probe, dimensions: { ...probe.dimensions, y: h * IN } },
        ctx
      ).sku
  );
  if (made) return null;
  return `${family.vendor || family.name} makes no tall pantry ${said(asIn(probe.dimensions.x))} wide`;
}

/**
 * The range the line makes this kind in, as a sentence — "Venus Cabinetry
 * makes drawer bases from 12" to 36"." — or null for a family with no
 * catalog. Said when a drag pushes past the kind's narrowest or widest.
 */
function kindRangeSentence(rule, { belowIn } = {}) {
  if (!rule.line) return null;
  const { vendor, many } = rule.line;
  // Pushed below the narrowest it makes, onto a width the line makes only as
  // a full-height door: say that, since "from 12"" reads as if no 9" exists.
  const fullDoor =
    belowIn != null && belowIn < (rule.minIn ?? 0)
      ? (rule.fullDoorOnlyIn || []).filter((d) => d < rule.minIn)
      : [];
  if (fullDoor.length) return `${fullDoorOnlySentence(vendor, fullDoor[0])}.`;
  const w = rule.widthsIn || [];
  if (!w.length) return `${rule.noneReason || `${vendor} makes no ${many}`}.`;
  if (w.length === 1) return `${vendor} makes ${many} only ${said(w[0])} wide.`;
  return `${vendor} makes ${many} from ${said(w[0])} to ${said(w[w.length - 1])}.`;
}

/**
 * The width the run's OWN fill should build a box it sized `widthIn` wide when
 * that box wears `front` — or null when it may stay as the packer laid it: a
 * family with no catalog, a front this rule does not cover (a filler, a tall
 * span), a width the line makes, or a kind it makes at no width at all.
 *
 * Shrinks first — the widest width the line makes this kind at that is no
 * wider than the box was, so the rest of the stretch only gains room — and
 * grows to the kind's narrowest only when nothing narrower is made; the
 * caller must check a grown box still fits its stretch.
 */
function kindFitWidthIn(run, cabinet, widthIn, { front } = {}) {
  const rule = bayWidthRule(run, cabinet, { front });
  if (!rule.line || allowsWidthIn(rule, widthIn)) return null;
  return (
    fitAllowedIn(rule, widthIn, { atMostIn: widthIn + SAME_IN }) ??
    fitAllowedIn(rule, widthIn)
  );
}

/**
 * The width a stored bay override becomes in `run` — the one write-boundary
 * snap. `cabinet` is the box the override governs (its front and depth say
 * which kind it is); `front` the front being written with it, if any.
 */
function snapBayWidthIn(run, cabinet, widthIn, { front } = {}) {
  return nearestAllowedIn(bayWidthRule(run, cabinet, { front }), widthIn);
}

/**
 * A stand-in box for an override whose cabinet is not at hand (a family
 * re-fit reads override records, not objects): a lower at the override's
 * width, the run's body height and depth, wearing the override's front.
 */
function standInBoxFor(override, run) {
  const box = {
    type: override?.tier === 'upper' ? 'upper-cabinet' : 'lower-cabinet',
    label: 'Base Cabinet',
    category: {},
    dimensions: {
      x: override?.width || 0,
      y: runBodyHeight(run),
      z: runDepthSpec(run),
    },
  };
  if (override?.type && box.type === 'lower-cabinet') {
    applyBayType(box, override.type);
  }
  return box;
}

module.exports = {
  bayWidthRule,
  allowsWidthIn,
  nearestAllowedIn,
  fitAllowedIn,
  widthUnavailableReason,
  frontWidthUnavailableReason,
  kindRangeSentence,
  kindFitWidthIn,
  snapBayWidthIn,
  standInBoxFor,
};
